import React from 'react';
import {get, defaultsDeep, merge} from 'lodash';
import {I18n} from 'react-redux-i18n';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import PropTypes from 'prop-types';

import moment from '../util/moment';
import formatOer from '../util/translateOer';
import Regulation from '../enum/Regulation';

function formatDuration(time, fromFormat = 'm') {
  const durationFormat = ['s', 'seconds'].includes(fromFormat) ? 'seconds' : 'minutes';
  const toFormat = I18n.t('hh:mm:ss');

  return moment.duration(time, durationFormat).format(toFormat, {
    trim: false
  });
}

class PlayerFullDetails extends React.Component {

  static propTypes = {
    media: PropTypes.shape({
      mediaId: PropTypes.string.isRequired,
      vehicle: PropTypes.string.isRequired,
      metadata: PropTypes.shape({
        link: PropTypes.shape({
          type: PropTypes.oneOf(['dsn', 'vin']),
          value: PropTypes.any
        }),
        triggerType: PropTypes.string.isRequired,
        remainingDrivingTime: PropTypes.any,
        remainingOnDutyTime: PropTypes.any
      }).isRequired,
      captures: PropTypes.arrayOf(
        PropTypes.shape({
          durationSeconds: PropTypes.number.isRequired,
          startDate: PropTypes.any.isRequired,
          endDate: PropTypes.any.isRequired
        })
      )
    }).isRequired,
    clip: PropTypes.shape({
      size: PropTypes.number.isRequired
    }).isRequired,
    visible: PropTypes.bool
  }

  static defaultProps = {
    media: {
      captures: [],
      metadata: {
        link: {
          type: 'dsn'
        }
      }
    },
    visible: false
  }

  constructor(props) {
    defaultsDeep(props, PlayerFullDetails.defaultProps);
    super(props);
  }

  renderDvrIdentification() {
    const {media} = this.props;

    const link = merge({
      type: 'dsn',
      value: media.dsn
    }, get(media, 'metadata.link', {}));

    return (
      <TableRow>
        <TableCell className="fulldetailstabledesktop__firstcol">
          {I18n.t(`page.playerPage.mediaDetails.${link.type}`)}
        </TableCell>

        <TableCell id={link.type}>
          {link.value}
        </TableCell>
      </TableRow>
    );
  }

  renderDateAndTime = () => {
    const {mediaDate} = this.props.media;

    if (mediaDate) {
      const date = parseInt(mediaDate, 10);

      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.dateAndTime')}
          </TableCell>

          <TableCell id='dateAndTime'>
            {moment(date).format(I18n.t('configuration.dateTimeFormat'))}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderStartTime = () => {
    const {captures} = this.props.media;

    if (captures[0]) {
      const date = parseInt(captures[0].startDate, 10);

      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.startTime')}
          </TableCell>

          <TableCell id="startTime">
            {moment(date).format(I18n.t('configuration.timeFormat'))}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderEndTime = () => {
    const {captures} = this.props.media;

    if (captures[0]) {
      const date = parseInt(captures[0].endDate, 10);

      return (

        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.endTime')}
          </TableCell>

          <TableCell id='endTime'>
            {moment(date).format(I18n.t('configuration.timeFormat'))}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderTerminal = () => {
    const terminal = get(this.props, 'media.metadata.terminal', null);

    if (terminal) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.terminal')}
          </TableCell>

          <TableCell id='terminal'>
            {terminal.name}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderLocation = () => {
    const {address} = this.props.media;

    if (address) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.location')}
          </TableCell>

          <TableCell id='location'>
            {address.street}, {address.city}, {address.state}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderGeolocation = () => {
    const {location} = this.props.media;

    if (location) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.geolocation')}
          </TableCell>

          <TableCell id='geolocation'>
            {location.lat}, {location.lon}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderVideoSize = () => {
    const {clip} = this.props;

    return `${Math.round(clip.size * 100) / 100} MB`;
  }

  renderTriggerTypeAndOer = () => {
    const {relatedItems} = this.props.media;
    const {metadata} = this.props.media;

    const renderTriggerType = () => {
      if (metadata.triggerType === 'onDemand') {
        return null;
      } else if (relatedItems && relatedItems.oerEvent) {
        return `${formatOer(relatedItems.oerEvent.trigger)} / `;
      } else if (metadata.EventType) {
        return `${metadata.EventType}} / `;
      }
      return null;
    };


    return (
      <TableRow>
        <TableCell className="fulldetailstabledesktop__firstcol">
          {I18n.t('page.playerPage.mediaDetails.oerType')}
        </TableCell>

        <TableCell id='oerType'>
          {renderTriggerType()}
          {this.props.media.metadata.triggerType}
        </TableCell>
      </TableRow>
    );
  }

  renderWifi = () => {
    const {metadata} = this.props.media;

    if (metadata.forceWifiUpload) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.uploadType.title')}
          </TableCell>

          <TableCell id='title'>
            {I18n.t('page.playerPage.mediaDetails.uploadType.wifiOnDemand')}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderDriverName = () => {
    const {metadata} = this.props.media;

    if (metadata.driverName1) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.driver')}
          </TableCell>

          <TableCell id='driver'>
            {metadata.driverName1}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderDriverId = () => {
    const {driverNumber1} = this.props.media.metadata;

    if (driverNumber1) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.driverId')}
          </TableCell>

          <TableCell id='driverId'>
            {driverNumber1}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderReviewerName = () => {
    const {review} = this.props;

    if (review) {
      return (
        <TableRow >
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.reviewerName')}
          </TableCell>

          <TableCell id='reviewerName'>
            {review.reviewerName}
          </TableCell>
        </TableRow >
      );
    }

    return null;
  }

  renderDriverStatus = () => {
    const {driverStatus} = this.props.media.metadata;

    if (driverStatus) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.driverStatus')}
          </TableCell>

          <TableCell id='driverStatus'>
            {this.props.media.metadata.driverStatus}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderAwfTripId = () => {
    const {metadata} = this.props.media;

    if (metadata.awfTripId) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.awfTripId')}
          </TableCell>

          <TableCell id='awfTripId'>
            {metadata.awfTripId}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderAmbientTemp = () => {
    const {metadata} = this.props.media;

    if (metadata.ambientTemp) {
      return (
        <TableRow>
          <TableCell className="fulldetailstabledesktop__firstcol">
            {I18n.t('page.playerPage.mediaDetails.ambientTemp')}
          </TableCell>

          <TableCell id='ambientTemp'>
            {metadata.ambientTemp}
          </TableCell>
        </TableRow>
      );
    }

    return null;
  }

  renderCurrentRegulation() {
    const {metadata: {currentReg}} = this.props.media;

    if (!currentReg) {
      return null;
    }

    return (
      <TableRow>
        <TableCell className="fulldetailstabledesktop__firstcol">
          {I18n.t('page.playerPage.mediaDetails.currentReg')}
        </TableCell>

        <TableCell id="currentReg">
          {Regulation.forCurrent(currentReg)}
        </TableCell>
      </TableRow>
    );
  }

  renderRemainingDrivingTime = () => {
    const {metadata} = this.props.media;

    if (!metadata.remainingDrivingTime) {
      return null;
    }

    return (
      <TableRow>
        <TableCell className="fulldetailstabledesktop__firstcol">
          {I18n.t('page.playerPage.mediaDetails.remainingDrivingTime')}
        </TableCell>

        <TableCell id="remainingDrivingTime">
          <span>{formatDuration(metadata.remainingDrivingTime, metadata.remainingTimeFormat)}</span>
          <span> {I18n.t('defaults.hoursMinutesSecondsFormat')}</span>
        </TableCell>
      </TableRow>
    );
  }

  renderRemainingOnDutyTime = () => {
    const {metadata} = this.props.media;

    if (!metadata.remainingOnDutyTime) {
      return null;
    }

    return (
      <TableRow>
        <TableCell className="fulldetailstabledesktop__firstcol">
          {I18n.t('page.playerPage.mediaDetails.remainingOnDutyTime')}
        </TableCell>

        <TableCell id="remainingOnDutyTime">
          <span>{formatDuration(metadata.remainingOnDutyTime, metadata.remainingTimeFormat)}</span>
          <span> {I18n.t('defaults.hoursMinutesSecondsFormat')}</span>
        </TableCell>
      </TableRow>
    );
  }

  render() {
    if (!this.props.visible) return null;

    return (
      <div>
        <div className="fulldetailstabledesktop__cont">
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className="fulldetailstabledesktop__firstcol">
                  {I18n.t('page.playerPage.mediaDetails.mediaId')}
                </TableCell>

                <TableCell id='mediaId'>
                  {this.props.media.mediaId}
                </TableCell>
              </TableRow>

              {this.renderDateAndTime()}
              {this.renderStartTime()}
              {this.renderEndTime()}

              {this.renderDvrIdentification()}

              {this.renderTerminal()}

              <TableRow>
                <TableCell className="fulldetailstabledesktop__firstcol">
                  {I18n.t('page.playerPage.mediaDetails.vehicle')}
                </TableCell>

                <TableCell id='vehicle'>
                  {this.props.media.vehicle}
                </TableCell>
              </TableRow>

              {this.renderLocation()}
              {this.renderGeolocation()}

              <TableRow>
                <TableCell className="fulldetailstabledesktop__firstcol">
                  {I18n.t('page.playerPage.mediaDetails.duration')}
                </TableCell>

                <TableCell id='duration'>
                  {Math.floor(this.props.media.captures[0].durationSeconds)} {I18n.t('defaults.seconds')}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="fulldetailstabledesktop__firstcol">
                  {I18n.t('page.playerPage.mediaDetails.videoSize')}
                </TableCell>

                <TableCell id='videoSize'>
                  {this.renderVideoSize()}
                </TableCell>
              </TableRow>

              {this.renderTriggerTypeAndOer()}

              {this.renderWifi()}
              {this.renderDriverName()}
              {this.renderDriverId()}
              {this.renderReviewerName()}
              {this.renderAwfTripId()}
              {this.renderDriverStatus()}
              {this.renderAmbientTemp()}
              {this.renderCurrentRegulation()}
              {this.renderRemainingDrivingTime()}
              {this.renderRemainingOnDutyTime()}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
}

export default PlayerFullDetails;
